import {
  getSDK,
  awaitSDKInit,
  getUserProfileCached,
  listOrganizationsCached,
  invalidateUserDataCache
} from '../../shared/functions/sdk.js'
import {
  groupByOwner,
  ownerSlug,
  projectKey,
  projectScreenshotUrl
} from '../../shared/functions/projects.js'
import { getPref, setPref, ACTIVE_ORG } from '../../shared/prefs.js'
import { getAllProjectsCached } from './studios.js'

const slugify = (name) =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// ── Slug availability helpers (AC1 + AC2) ──────────────────────────────────
// Module-level debounce timer so rapid name input coalesces into one check.
let _slugDebounceTimer = null

// Check whether a slug is available.
// Returns true = available, false = taken, null = check failed.
//
// Routes through the SDK adapter (`getSDK().checkOrganizationSlug`) which
// hits `/organizations?slug=X&_check=1` and threads the SDK's auth header
// + base URL (channel-resolved). Was a raw `fetch(\`<hardcoded next>/core/...\`)`
// — replacing with the SDK keeps every call honoring the
// `workspaceApiBase` / channels override (localNext for dev).
async function _checkSlugAvailable (slug) {
  if (!slug) return null
  try {
    return await getSDK().checkOrganizationSlug(slug)
  } catch {
    return null
  }
}

// Find the first available slug: tries base, then base-2, base-3 … up to N.
async function _findAvailableSlug (base, maxAttempts = 20) {
  for (let n = 0; n < maxAttempts; n++) {
    const candidate = n === 0 ? base : `${base}-${n + 1}`
    if (await _checkSlugAvailable(candidate) === true) return candidate
  }
  return null
}

// Schedule a debounced slug availability check (350 ms).
// On slug_taken auto-advances to the first free -2/-3/-N suffix.
function _scheduleSlugCheck (rawSlug, root) {
  if (_slugDebounceTimer) clearTimeout(_slugDebounceTimer)
  const base = slugify(rawSlug)
  if (!base) {
    root.update({
      newOrgSlug: '',
      newOrgSlugChecking: false,
      newOrgSlugStatus: null,
      newOrgSlugSuggestion: null
    })
    return
  }
  root.update({ newOrgSlugChecking: true, newOrgSlugStatus: null })
  _slugDebounceTimer = setTimeout(async () => {
    _slugDebounceTimer = null
    const available = await _checkSlugAvailable(base)
    if (available === true) {
      root.update({
        newOrgSlug: base,
        newOrgSlugChecking: false,
        newOrgSlugStatus: 'available',
        newOrgSlugSuggestion: null
      })
    } else if (available === false) {
      const suggestion = await _findAvailableSlug(base)
      root.update({
        newOrgSlug: suggestion || base,
        newOrgSlugChecking: false,
        newOrgSlugStatus: suggestion ? 'suggested' : 'taken',
        newOrgSlugSuggestion: suggestion
      })
    } else {
      // Network error — allow user to proceed; server is the final arbiter.
      root.update({ newOrgSlugChecking: false, newOrgSlugStatus: 'error' })
    }
  }, 350)
}

// ── createOrganization (AC3 + AC4) ─────────────────────────────────────────
async function createOrganization(root) {
  const name = (root?.newOrgName || '').trim()
  // Use the live-check resolved slug when available; otherwise derive from name.
  const slug = (root?.newOrgSlug || slugify(name)).trim()
  if (!name || !slug) {
    root.update({ newOrgError: 'Name and slug are required' })
    return
  }
  root.update({ newOrgBusy: true, newOrgError: null, newOrgSlugConflict: null })
  try {
    const sdk = getSDK()
    const org = await sdk.createOrganization({ name, slug })
    // Reload projects so the new workspace shows up in the sidebar, then
    // activate it (even though it has no projects yet — the workspace page
    // will just render an empty board).
    await loadProjects({ root })
    const newSlug = org?.slug || slug
    setPref(ACTIVE_ORG, newSlug)
    root.update({
      activeWorkspace: newSlug,
      newOrgModalOpen: null,
      newOrgName: '',
      newOrgSlug: '',
      newOrgSlugDirty: false,
      newOrgSlugChecking: false,
      newOrgSlugStatus: null,
      newOrgSlugSuggestion: null,
      newOrgSlugConflict: null,
      newOrgBusy: false
    })
  } catch (err) {
    // AC4: detect slug_taken specifically — never swallow it as a generic error.
    const isSlugTaken =
      err?.message === 'slug_taken' ||
      err?.message?.includes('slug_taken') ||
      err?.message?.toLowerCase().includes('slug') && err?.message?.toLowerCase().includes('taken')
    if (isSlugTaken) {
      // AC2 / AC3: race-condition 409 after live check passed. Find next free
      // suffix and surface a one-click "Use <slug-N>" button (never silent).
      const suggestion = await _findAvailableSlug(slug)
      root.update({
        newOrgBusy: false,
        newOrgError: `Slug "${slug}" was just taken.`,
        newOrgSlugConflict: slug,
        newOrgSlug: suggestion || slug,
        newOrgSlugSuggestion: suggestion,
        newOrgSlugStatus: suggestion ? 'suggested' : 'taken'
      })
      return
    }
    root.update({
      newOrgBusy: false,
      newOrgError: err?.message || 'Failed to create organization'
    })
  }
}

const VISIBLE_PER_ROW = 6

async function loadProjects(s, el) {
  const root = s.root || s
  try {
    const sdk = getSDK()
    // Wait for the first init promise — calling initialize() again would
    // wipe the auth session per sdk.js notes.
    await awaitSDKInit()

    const token = sdk.getAuthToken?.()
    if (!token) {
      if (el?.router) el.router('/signin', el.getRoot())
      else root.update({ projects: [], projectsError: 'Not signed in' })
      return
    }

    // `/projects` has canonical owner info (user vs org); `getUserProjects()`
    // flattens every project onto a user-level owner even when it's org-owned
    // and that breaks workspace grouping. Let errors from these SDK calls
    // bubble to the outer catch so the UI surfaces a real reason instead of
    // silently rendering "0 workspaces". The profile + orgs fetches are
    // sessionStorage-cached to keep multi-tab reloads from hammering the API
    // past its rate limit.
    const [profile, orgs, all] = await Promise.all([
      getUserProfileCached(),
      listOrganizationsCached(),
      getAllProjectsCached()
    ])

    const userSlug = profile?.username
    // `listOrganizations` returns every org in the system (each user has
    // their own `type: 'personal'` shadow org). Filter down to orgs the
    // signed-in user actually owns or is a member of — and drop the user's
    // own `type: 'personal'` shadow org so it doesn't collide with the
    // real personal workspace row in the sidebar.
    const myOrgs = (orgs || []).filter((o) => {
      const mine =
        o?.ownerUser?.username === userSlug ||
        o?.owner?.username === userSlug ||
        (o?.members || []).some((m) => m?.user?.username === userSlug)
      if (!mine) return false
      if (o?.type === 'personal' && o?.slug === userSlug) return false
      return true
    })
    const orgSlugs = new Set(myOrgs.map((o) => o.slug).filter(Boolean))

    const projects = (all || []).filter((p) => {
      const o = p?.owner
      if (!o) return false
      if (o.type === 'organization') return orgSlugs.has(o.slug)
      return o.username && o.username === userSlug
    })
    // Default active workspace = first workspace (user's personal if present).
    const workspaces = groupByOwner(projects)
    const savedOrg = getPref(ACTIVE_ORG)
    const savedValid =
      savedOrg &&
      (savedOrg === userSlug ||
        myOrgs.some((o) => o.slug === savedOrg) ||
        workspaces.some((w) => w.owner === savedOrg))
    const activeWorkspace =
      root.activeWorkspace ||
      (savedValid ? savedOrg : null) ||
      workspaces.find((w) => w.owner === userSlug)?.owner ||
      workspaces[0]?.owner ||
      null
    root.update({
      projects,
      orgs: myOrgs,
      activeWorkspace,
      userSlug,
      projectsError: null
    })
    // The dashboard renders only the sidebar + an empty StudiosEmbed
    // placeholder — it does NOT show the workspace board, so we avoid
    // pre-warming loadNodes here. doLoadNodes triggers fetchServerPositions
    // which fans out one full getProjectData call per own project (just to
    // read canvasPosition), which manifests as a request storm on dashboards
    // with even a handful of projects. The studios page calls loadNodes
    // itself once the user actually navigates to /studio/{slug}.
    if (activeWorkspace) loadOrgMembers(root, activeWorkspace)
  } catch (err) {
    console.warn('[dashboard] loadProjects failed:', err)
    const msg = err?.message || ''
    const isAuthErr = /no token|log in|unauthorized|unauthenticated/i.test(msg)
    if (isAuthErr && el?.router) {
      // Clear the stale token so applyAuthGuard won't bounce back to /dashboard
      // on the next full page load while at /signin.
      try {
        getSDK().logout()
      } catch {}
      invalidateUserDataCache()
      el.router('/signin', el.getRoot())
      return
    }
    root.update({
      projects: [],
      projectsError: msg || 'Failed to load projects'
    })
  }
}

// Per MODEL.md an Organization owns 1..N Workspaces. Fetch the list on
// demand when the org row expands so the picker knows whether to show
// "default" (solo workspace) or a multi-pick strip.
async function loadOrgWorkspaces(root, org) {
  if (!org?.id) return
  const orgId = org.id
  const existing = root?.workspacesByOrg || {}
  if (existing[orgId]) return
  try {
    const sdk = getSDK()
    const res = await sdk.listWorkspaces({ organization: orgId })
    const list = Array.isArray(res) ? res : (res?.data || res?.workspaces || [])
    root.update({
      workspacesByOrg: { ...(root.workspacesByOrg || {}), [orgId]: list }
    })
  } catch (err) {
    console.warn('[dashboard] loadOrgWorkspaces failed:', org.slug, err?.message || err)
    // Cache an empty array so we don't retry on every expand — user can
    // refresh to retry.
    root.update({
      workspacesByOrg: { ...(root.workspacesByOrg || {}), [orgId]: [] }
    })
  }
}

async function createOrgWorkspace(root, org) {
  if (!org?.id) return
  const name = (window.prompt('Name for this workspace?', 'New workspace') || '').trim()
  if (!name) return
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || null
  try {
    const sdk = getSDK()
    const created = await sdk.createWorkspace({
      organization: org.id,
      displayName: name,
      slug
    })
    const ws = created?.data || created
    const prev = root.workspacesByOrg?.[org.id] || []
    root.update({
      workspacesByOrg: { ...(root.workspacesByOrg || {}), [org.id]: [...prev, ws] },
      activeWorkspaceId: ws?._id || ws?.id || null
    })
  } catch (err) {
    window.alert('Create workspace failed: ' + (err?.message || err))
  }
}

async function loadOrgMembers(root, slug) {
  const org = (root?.orgs || []).find((o) => o.slug === slug)
  if (!org) {
    // Slug isn't an org (it's the user's personal workspace) — clear the
    // stale member list so the PeopleSection hides.
    root.update({ orgMembers: null, orgMembersSlug: slug })
    return
  }
  if (root?.orgMembersSlug === slug && root?.orgMembers) return
  root.update({ orgMembers: null, orgMembersSlug: slug })
  try {
    const sdk = getSDK()
    const members = await sdk.listOrgMembers(org.id)
    // `listOrgMembers` sometimes returns the bare array, sometimes `{data}`.
    const list = Array.isArray(members) ? members : members?.data || []
    // Only commit if the active workspace hasn't changed in the meantime.
    if (root?.activeWorkspace === slug) {
      root.update({ orgMembers: list, orgMembersSlug: slug })
    }
  } catch (err) {
    console.warn('[dashboard] loadOrgMembers failed:', err)
    if (root?.activeWorkspace === slug) {
      root.update({ orgMembers: [], orgMembersSlug: slug })
    }
  }
}

const workspaceSummary = (s) => {
  if (s.root?.projectsError) return 'Error: ' + s.root.projectsError
  if (!s.root?.projects) return 'Loading your projects…'
  const n = s.root.projects.length
  return n
    ? `${n} project${n === 1 ? '' : 's'} across your workspaces`
    : 'No projects yet'
}

// The signed-in user's personal workspace — at most one entry. Returns an
// empty-items placeholder if the user has no personal projects yet so the
// row still renders.
const personalWorkspaces = (root) => {
  const userSlug = root?.userSlug
  if (!userSlug) return []
  const grouped = groupByOwner(root?.projects || [])
  const personal = grouped.find((w) => w.owner === userSlug)
  return personal ? [personal] : [{ owner: userSlug, items: [] }]
}

// Orgs the user owns or is a member of. `root.orgs` is pre-filtered in
// `loadProjects`; we additionally drop any `type: 'personal'` shadow org
// whose slug matches the user's username so it doesn't duplicate the
// personal workspace row.
const orgWorkspaces = (root) => {
  const userSlug = root?.userSlug
  const userOrgs = (root?.orgs || []).filter(
    (o) => !(o?.type === 'personal' && o?.slug === userSlug)
  )
  const allowed = new Set(userOrgs.map((o) => o.slug).filter(Boolean))
  const orgsWithProjects = groupByOwner(root?.projects || []).filter(
    (w) => w.owner !== userSlug && allowed.has(w.owner)
  )
  const seen = new Set(orgsWithProjects.map((w) => w.owner))
  const emptyOrgs = userOrgs
    .filter((o) => o.slug && !seen.has(o.slug))
    .map((o) => ({ owner: o.slug, items: [] }))
  return [...orgsWithProjects, ...emptyOrgs]
}

const activeOrg = (root) =>
  (root?.orgs || []).find((o) => o.slug === root?.activeWorkspace) || null

export const dashboard = {
  tag: 'main',
  id: 'main-content',
  extends: 'AppPage',
  metadata: { title: 'Dashboard / Symbols.app' },
  gridRow: '2',
  gridColumn: '1 / -1',
  minHeight: '0',
  height: '100%',
  overflow: 'hidden',
  color: 'title',

  onRender: (el, s) => {
    if (el.__dashLoaded) return
    el.__dashLoaded = true
    loadProjects(s, el)
  },

  AppSidebar: {
    WorkspacesSection: {
      flow: 'y',
      gap: 'Y',

      PersonalCaption: { extends: 'CaptionTitle', Text: { text: 'Personal' } },
      PersonalList: {
        flow: 'y',
        gap: 'V',
        children: (el, s) => personalWorkspaces(s.root),
        childrenAs: 'state',
        childExtends: 'WorkspaceRowButton'
      },

      OrgsCaption: {
        extends: 'CaptionTitle',
        marginTop: 'Z',
        Text: { text: 'Organizations' }
      },
      OrgsList: {
        flow: 'y',
        gap: 'V',
        children: (el, s) => orgWorkspaces(s.root),
        childrenAs: 'state',
        childExtends: 'OrgRow'
      },

      NewOrgButton: {
        tag: 'button',
        type: 'button',
        marginTop: 'Y',
        padding: 'Y Z1',
        round: 'Z',
        fontSize: 'Z1',
        fontWeight: '500',
        color: 'caption',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        cursor: 'pointer',
        border: '0',
        background: 'overlay',
        ':hover': { background: 'overlayStrong', color: 'title' },
        onClick: (e, el, s) =>
          s.root.update({ newOrgModalOpen: true, newOrgError: null }),
        PlusIcon: { tag: 'span', text: '+', fontWeight: '600' },
        Label: { tag: 'span', text: 'New organization' }
      }
    },

    // Marketplace browse section. Mirrors MarketplaceSidebar's KINDS so
    // dashboard users can jump straight into a kind-filtered marketplace
    // view without first opening /marketplace and scanning the sidebar.
    // Each row deep-links to /marketplace?kind=X — the canvas marketplace
    // page reads the param on mount and seeds `root.activeKind`.
    MarketplaceSection: {
      flow: 'y',
      gap: 'Y',
      marginTop: 'Z',

      MarketplaceCaption: {
        extends: 'CaptionTitle',
        Text: { text: 'Marketplace' }
      },
      MarketplaceList: {
        flow: 'y',
        gap: 'V',
        children: (el, s) => [
          { kind: 'all', label: 'Browse all' },
          { kind: 'library', label: 'Libraries' },
          { kind: 'template', label: 'Templates' },
          { kind: 'integration', label: 'Integrations' },
          { kind: 'registrar', label: 'Domain Registrars' },
          { kind: 'plugin', label: 'Plugins' }
        ],
        childrenAs: 'state',
        childExtends: 'MarketplaceKindLink'
      }
    },

    // People in the active org — shown only when `activeWorkspace` matches a
    // known organization slug. Extends the shared `PeopleSection` shell
    // (scrollable column) that governance uses for its own sidebar. We
    // override its `flex: 1` so when the member list is short the section
    // sits intrinsic-height near the top; when it's long enough to overflow,
    // the parent flex still lets it grow + scroll inside.
    PeopleSection: {
      extends: 'PeopleSection',
      hide: (el, s) => !activeOrg(s.root),
      flex: '0 1 auto',
      minHeight: '0',
      maxHeight: '50%',

      TeamGroup: {
        extends: 'PeopleGroup',
        groupName: 'team',
        CaptionTitle: {
          Text: {
            text: (el, s) => (activeOrg(s.root)?.name || 'People').toUpperCase()
          }
        },
        List: {
          children: (el, s) => s.root?.orgMembers || []
        }
      },

      Loading: {
        if: (el, s) => activeOrg(s.root) && s.root?.orgMembers == null,
        tag: 'span',
        padding: 'Y Z1',
        fontSize: 'Z',
        color: 'caption',
        text: 'Loading…'
      },

      Empty: {
        if: (el, s) =>
          activeOrg(s.root) &&
          Array.isArray(s.root?.orgMembers) &&
          s.root.orgMembers.length === 0,
        tag: 'span',
        padding: 'Y Z1',
        fontSize: 'Z',
        color: 'caption',
        text: 'No members yet'
      }
    }
  },

  AppMain: {
    padding: '0',
    margin: '0',
    overflow: 'hidden',

    Body: {
      padding: '0',
      overflow: 'hidden',

      StudiosEmbed: {
        position: 'relative',
        width: '100%',
        height: '100%'
      }
    }
  },

  NewOrgModal: {
    extends: 'Modal',
    stateKey: 'newOrgModalOpen',
    // Reset all new-org state when modal is closed.
    extraClose: {
      newOrgError: null,
      newOrgName: '',
      newOrgSlug: '',
      newOrgSlugDirty: false,
      newOrgSlugChecking: false,
      newOrgSlugStatus: null,
      newOrgSlugSuggestion: null,
      newOrgSlugConflict: null
    },

    Window: {
      extends: 'ModalWindow',
      ModalHeader: { title: 'New organization' },

      Hint: {
        tag: 'p',
        text: 'Creates a new workspace you own. You can invite members and add projects afterwards.',
        fontSize: 'Z',
        color: 'caption',
        margin: '0'
      },

      NameField: {
        flow: 'y',
        gap: 'V',

        Label: { tag: 'label', text: 'Name', fontSize: 'Z', color: 'caption' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'Acme Inc',
          padding: 'Y Z1',
          round: 'Z',
          border: '1px solid line',
          background: 'overlay',
          color: 'title',
          fontSize: 'Z1',
          value: (el, s) => s.root?.newOrgName || '',
          autofocus: true,
          onInput: (e, el, s) => {
            const name = e.target.value
            const wasSlugDirty = !!s.root?.newOrgSlugDirty
            const patch = { newOrgName: name, newOrgError: null, newOrgSlugConflict: null }
            // AC1: auto-derive slug from name and trigger live availability
            // check as long as the user hasn't manually edited the slug field.
            if (!wasSlugDirty) {
              patch.newOrgSlug = slugify(name)
              patch.newOrgSlugDirty = false
            }
            s.root.update(patch)
            // Sync slug input DOM value immediately when auto-deriving — onRender
            // only fires on explicit DOMQL re-renders so the input would otherwise
            // keep showing the old/placeholder value until the next full render cycle.
            if (!wasSlugDirty) {
              const derivedSlug = slugify(name)
              const slugInput = el.getRoot()?.node?.querySelector('input[name="newOrgSlug"]')
              if (slugInput) slugInput.value = derivedSlug
            }
            // Schedule availability check using the resolved slug.
            if (!wasSlugDirty) {
              _scheduleSlugCheck(name, s.root)
            }
          }
        }
      },

      // AC1: slug field with live availability indicator (debounced 350 ms).
      SlugField: {
        flow: 'y',
        gap: 'V',

        Label: {
          tag: 'label',
          text: 'Slug (used in URLs)',
          fontSize: 'Z',
          color: 'caption'
        },

        SlugRow: {
          flow: 'x',
          gap: 'Z',
          align: 'center left',

          Input: {
            tag: 'input',
            name: 'newOrgSlug',
            type: 'text',
            placeholder: 'acme',
            padding: 'Y Z1',
            round: 'Z',
            border: (el, s) => {
              const st = s.root?.newOrgSlugStatus
              if (st === 'taken') return '1px solid red'
              if (st === 'available') return '1px solid green'
              return '1px solid line'
            },
            background: 'overlay',
            color: 'title',
            fontSize: 'Z1',
            fontFamily: 'monospace',
            flex: '1',
            // Keep DOM value in sync when auto-derived slug changes.
            onRender: (el, s) => {
              if (s.root?.newOrgSlugDirty) return
              const slug = s.root?.newOrgSlug || ''
              if (el.node && el.node.value !== slug) el.node.value = slug
            },
            onInput: (e, el, s) => {
              const val = slugify(e.target.value)
              e.target.value = val
              s.root.update({
                newOrgSlug: val,
                newOrgSlugDirty: true,
                newOrgError: null,
                newOrgSlugConflict: null
              })
              // AC1: trigger availability check on manual slug edit.
              _scheduleSlugCheck(val, s.root)
            }
          },

          // AC1: inline status indicator (available / taken / auto-adjusted).
          SlugStatus: {
            tag: 'span',
            fontSize: 'Y2',
            whiteSpace: 'nowrap',
            hide: (el, s) => !s.root?.newOrgSlug,
            color: (el, s) => {
              if (s.root?.newOrgSlugChecking) return 'caption'
              const st = s.root?.newOrgSlugStatus
              if (st === 'available') return 'green'
              if (st === 'taken') return 'red'
              if (st === 'suggested') return 'yellow'
              return 'caption'
            },
            text: (el, s) => {
              if (s.root?.newOrgSlugChecking) return 'checking…'
              const st = s.root?.newOrgSlugStatus
              if (st === 'available') return 'available'
              if (st === 'taken') return 'taken'
              if (st === 'suggested') return 'auto-adjusted'
              return ''
            }
          }
        },

        // AC2 / AC3: shown when live check or POST found slug taken.
        // Displays a one-click "Use <slug-N>" button for the auto-suffix.
        SlugHint: {
          hide: (el, s) => {
            const st = s.root?.newOrgSlugStatus
            const conflict = s.root?.newOrgSlugConflict
            return st !== 'suggested' && st !== 'taken' && !conflict
          },
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          margin: 'V 0 0',

          HintText: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            flex: '1',
            text: (el, s) => {
              const sug = s.root?.newOrgSlugSuggestion
              const conflict = s.root?.newOrgSlugConflict
              if (conflict && sug) return `"${conflict}" was just taken — try "${sug}" instead.`
              if (conflict) return `"${conflict}" is taken. Edit the slug above to pick a different one.`
              if (sug) return `Slug auto-adjusted to "${sug}" — available and ready.`
              return 'This slug is taken. Edit it above.'
            }
          },

          // One-click: apply the suggested suffix directly.
          UseSlugBtn: {
            tag: 'button',
            type: 'button',
            hide: (el, s) => !s.root?.newOrgSlugSuggestion,
            padding: 'V Z1',
            round: 'Y',
            background: 'overlay',
            color: 'title',
            fontWeight: '600',
            fontSize: 'Y2',
            border: '1px solid line',
            cursor: 'pointer',
            ':hover': { background: 'overlay' },
            text: (el, s) => {
              const sug = s.root?.newOrgSlugSuggestion
              return sug ? `Use "${sug}"` : ''
            },
            onClick: (e, el, s) => {
              e.preventDefault()
              const sug = s.root?.newOrgSlugSuggestion
              if (!sug) return
              s.root.update({
                newOrgSlug: sug,
                newOrgSlugDirty: true,
                newOrgSlugStatus: 'available',
                newOrgSlugSuggestion: null,
                newOrgSlugConflict: null,
                newOrgError: null
              })
              // Sync DOM input value immediately.
              const slugInput = el.getRoot()?.node?.querySelector('input[name="newOrgSlug"]')
              if (slugInput) slugInput.value = sug
            }
          }
        }
      },

      Error: {
        if: (el, s) => !!s.root?.newOrgError,
        tag: 'p',
        text: (el, s) => s.root?.newOrgError,
        color: 'red',
        fontSize: 'Z',
        margin: 'Z 0 0'
      },

      ModalFooter: {
        extends: 'ModalFooter',
        flow: 'x',
        gap: 'Y',
        align: 'center right',

        Cancel: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'Y A',
          round: 'Z',
          border: '1px solid line',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          ':hover': { background: 'overlay' },
          onClick: (e, el, s) =>
            s.root.update({
              newOrgModalOpen: null,
              newOrgError: null,
              newOrgName: '',
              newOrgSlug: '',
              newOrgSlugDirty: false,
              newOrgSlugChecking: false,
              newOrgSlugStatus: null,
              newOrgSlugSuggestion: null,
              newOrgSlugConflict: null
            })
        },

        Create: {
          tag: 'button',
          type: 'submit',
          text: (el, s) => (s.root?.newOrgBusy ? 'Creating…' : 'Create'),
          padding: 'Y A',
          round: 'Z',
          border: '0',
          background: 'blue',
          color: 'white',
          cursor: 'pointer',
          fontWeight: '500',
          // AC1: disable while slug check is in flight so user can't race-submit.
          disabled: (el, s) =>
            !!s.root?.newOrgBusy ||
            !!s.root?.newOrgSlugChecking ||
            s.root?.newOrgSlugStatus === 'taken' ||
            !(
              (s.root?.newOrgName || '').trim() &&
              (s.root?.newOrgSlug || '').trim()
            ),
          ':hover': { background: 'blue+8' },
          onClick: (e, el, s) => createOrganization(s.root)
        }
      }
    }
  }
}

const selectWorkspace = (root, owner) => {
  root.update({ activeWorkspace: owner })
  setPref(ACTIVE_ORG, owner)
  // No pre-warm of loadNodes here — the dashboard sidebar doesn't render
  // workspace nodes/positions/integrations, and doLoadNodes triggers an
  // expensive getProjectData fan-out per own project (see comment in
  // loadProjects above). The studios page loads on demand when the user
  // navigates to /studio/{slug}.
  loadOrgMembers(root, owner)
}

const workspaceRowStyle = {
  tag: 'button',
  type: 'button',
  padding: 'Y Z1',
  round: 'Z',
  fontSize: 'Z1',
  color: 'title',
  flow: 'x',
  align: 'center space-between',
  gap: 'Z',
  cursor: 'pointer',
  border: '0',
  textAlign: 'left',
  background: (el, s) =>
    s.root?.activeWorkspace === s.owner ? 'white.1' : 'transparent',
  fontWeight: (el, s) => (s.root?.activeWorkspace === s.owner ? '600' : '500'),
  ':hover': { background: 'overlay' }
}

export const WorkspaceRowButton = {
  ...workspaceRowStyle,
  onClick: (e, el, s) => selectWorkspace(s.root, s.owner),
  Name: { tag: 'span', text: (el, s) => s.owner, flex: '1' },
  Count: {
    tag: 'span',
    fontSize: 'Z',
    color: 'caption',
    text: (el, s) => String(s.items?.length || 0)
  }
}

// Expandable sidebar row — wraps WorkspaceRowButton and reveals the org's
// projects as deep-linkable rows when the caret is toggled. The button
// header still sets `activeWorkspace` (embedded canvas scope); the caret
// is a SECOND affordance so the user can jump straight into a project.
// Expansion state lives in `root.expandedOrgs[owner]` so it survives
// re-render and is isolated per org.
export const OrgRow = {
  flow: 'y',
  gap: 'V',

  Header: {
    ...workspaceRowStyle,
    onClick: (e, el, s) => selectWorkspace(s.root, s.owner),

    Name: { tag: 'span', text: (el, s) => s.owner, flex: '1' },
    Count: {
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      text: (el, s) => String(s.items?.length || 0)
    },
    Caret: {
      tag: 'span',
      padding: 'V',
      margin: '- -V - -',
      round: 'X',
      fontSize: 'Z',
      color: 'caption',
      cursor: 'pointer',
      transition: 'B transform defaultBezier',
      transform: (el, s) =>
        s.root?.expandedOrgs?.[s.owner] ? 'rotate(180deg)' : 'rotate(0deg)',
      ':hover': { color: 'title' },
      text: '▾',
      onClick: (e, el, s) => {
        e.stopPropagation()
        const cur = s.root?.expandedOrgs || {}
        const willOpen = !cur[s.owner]
        s.root.update({ expandedOrgs: { ...cur, [s.owner]: willOpen } })
        if (willOpen) {
          const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
          if (org) loadOrgWorkspaces(s.root, org)
        }
      }
    }
  },

  // Workspace picker — hidden when org has 1 workspace (default only);
  // shown as chips + "+" when 2+. Per MODEL.md each Workspace = separate
  // subscription, so "+" triggers a create flow (Pro/Scale upgrade
  // required on personal orgs — surfaced by the API if not allowed).
  WorkspacesPicker: {
    if: (el, s) => !!s.root?.expandedOrgs?.[s.owner],
    flow: 'x',
    gap: 'V',
    padding: 'V - V A',
    align: 'center left',
    flexWrap: 'wrap',

    DefaultChip: {
      if: (el, s) => {
        const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
        const list = org && s.root?.workspacesByOrg?.[org.id]
        return !list || list.length <= 1
      },
      tag: 'span',
      padding: 'V Y',
      round: 'X',
      fontSize: 'Z',
      color: 'caption',
      background: 'overlay',
      text: 'default'
    },

    Chips: {
      if: (el, s) => {
        const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
        const list = org && s.root?.workspacesByOrg?.[org.id]
        return !!(list && list.length > 1)
      },
      flow: 'x',
      gap: 'V',
      flexWrap: 'wrap',
      children: (el, s) => {
        const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
        return (org && s.root?.workspacesByOrg?.[org.id]) || []
      },
      childrenAs: 'state',
      childExtends: 'WorkspaceChip'
    },

    AddBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V Y',
      round: 'X',
      fontSize: 'Z',
      color: 'caption',
      background: 'transparent',
      border: '1px dashed line',
      cursor: 'pointer',
      ':hover': { color: 'title', background: 'overlay' },
      title: 'Create workspace',
      text: '+',
      onClick: (e, el, s) => {
        e.stopPropagation()
        const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
        if (org) createOrgWorkspace(s.root, org)
      }
    }
  },

  ProjectsList: {
    if: (el, s) => !!s.root?.expandedOrgs?.[s.owner],
    flow: 'y',
    gap: 'V',
    padding: 'V - V A',
    children: (el, s) => s.items || [],
    childrenAs: 'state',
    childExtends: 'OrgProjectRow'
  }
}

// Chip inside OrgRow.WorkspacesPicker.Chips — one per workspace.
// `state` is the Workspace record from listWorkspaces.
export const WorkspaceChip = {
  tag: 'button',
  type: 'button',
  padding: 'V Y',
  round: 'X',
  fontSize: 'Z',
  border: '0',
  cursor: 'pointer',
  background: (el, s) =>
    s.root?.activeWorkspaceId === (s._id || s.id) ? 'white.1' : 'overlay',
  color: (el, s) =>
    s.root?.activeWorkspaceId === (s._id || s.id) ? 'title' : 'caption',
  fontWeight: (el, s) =>
    s.root?.activeWorkspaceId === (s._id || s.id) ? '600' : '500',
  ':hover': { color: 'title', background: 'overlayStrong' },
  text: (el, s) => s.displayName || s.slug || 'workspace',
  onClick: (e, el, s) => {
    e.stopPropagation()
    s.root.update({ activeWorkspaceId: s._id || s.id || null })
  }
}

// Row inside an expanded OrgRow — one per project in the org.
export const OrgProjectRow = {
  tag: 'button',
  type: 'button',
  padding: 'V Z1',
  round: 'Z',
  fontSize: 'Z',
  color: 'caption',
  flow: 'x',
  align: 'center left',
  gap: 'Y',
  cursor: 'pointer',
  border: '0',
  textAlign: 'left',
  background: 'transparent',
  ':hover': { background: 'overlay', color: 'title' },
  onClick: (e, el, s) => {
    const owner = ownerSlug(s)
    const key = projectKey(s)
    if (owner && key) el.router(`/${owner}/${key}`, el.getRoot())
  },
  Name: {
    tag: 'span',
    text: (el, s) => s.name || s.key,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1'
  }
}

// Sidebar row — one per marketplace kind. Routes to /marketplace with
// `?kind=X` so the marketplace page can pre-select the active kind.
// `kind: 'all'` omits the param so /marketplace lands on its default
// All view. Shares hover/active styling with WorkspaceRowButton so the
// dashboard sidebar reads as one consistent column.
export const MarketplaceKindLink = {
  tag: 'button',
  type: 'button',
  padding: 'Y Z1',
  round: 'Z',
  fontSize: 'Z1',
  color: 'title',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  cursor: 'pointer',
  border: '0',
  textAlign: 'left',
  background: (el, s) => {
    const path = s.root?.currentPath || ''
    if (path !== '/marketplace') return 'transparent'
    const search = s.root?.currentSearch || ''
    const kind = new URLSearchParams(search).get('kind') || 'all'
    return kind === s.kind ? 'white.1' : 'transparent'
  },
  fontWeight: (el, s) => {
    const path = s.root?.currentPath || ''
    if (path !== '/marketplace') return '500'
    const search = s.root?.currentSearch || ''
    const kind = new URLSearchParams(search).get('kind') || 'all'
    return kind === s.kind ? '600' : '500'
  },
  ':hover': { background: 'overlay' },
  text: (el, s) => s.label,
  onClick: (e, el, s) => {
    const target = s.kind === 'all' ? '/marketplace' : `/marketplace?kind=${s.kind}`
    el.router(target, el.getRoot())
  }
}

// Sidebar row — one per org member. `state` is the member object from
// `sdk.listOrgMembers`; shape varies by server version.
export const OrgMemberRow = {
  tag: 'div',
  flow: 'x',
  gap: 'Y',
  align: 'center left',
  padding: 'Y Z1',
  round: 'Z',
  fontSize: 'Z',
  color: 'title',
  ':hover': { background: 'overlay' },

  Avatar: {
    extends: 'Avatar',
    avatarType: 'initials',
    boxSize: 'A A',
    src: (el, s) => el.call('avatarUrlFor', s?.email || s?.user?.email)
  },
  Info: {
    flow: 'y',
    gap: '0',
    flex: '1',
    minWidth: '0',

    Name: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      text: (el, s) =>
        s?.name || s?.user?.name || s?.email || s?.user?.email || '—',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    Role: {
      if: (el, s) => !!(s?.role || s?.membership?.role),
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => s?.role || s?.membership?.role
    }
  }
}

export const WorkspaceRow = {
  flow: 'column',
  gap: 'Y2',
  padding: 'A',
  round: 'C1',
  theme: 'canvas-card-glass',
  border: '1px solid rgba(255,255,255,0.08)',

  Head: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Y',
    cursor: 'pointer',
    onClick: (e, el, s) => el.router('/studio/' + s.owner, el.getRoot()),

    TitleRow: {
      flow: 'x',
      align: 'center',
      gap: 'Y',

      Name: {
        tag: 'h2',
        text: (el, s) => s.owner,
        fontSize: 'B',
        fontWeight: '600',
        letterSpacing: '-0.01em'
      },
      Count: {
        tag: 'span',
        text: (el, s) =>
          (s.items?.length || 0) +
          ' project' +
          (s.items?.length === 1 ? '' : 's'),
        fontSize: 'Z',
        color: 'rgba(255,255,255,0.5)',
        padding: 'W Y',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)'
      }
    },

    OpenBtn: {
      extends: 'Button',
      text: 'Open workspace →',
      padding: 'W Y',
      fontSize: 'Z',
      round: 'Z',
      border: '0',
      background: 'rgba(255,255,255,0.06)',
      color: 'white',
      ':hover': { background: 'rgba(255,255,255,0.12)' }
    }
  },

  Thumbs: {
    flow: 'x',
    gap: 'Y2',
    overflow: 'hidden',
    children: (el, s) => (s.items || []).slice(0, VISIBLE_PER_ROW),
    childrenAs: 'state',
    childExtends: 'WorkspaceThumb'
  },

  // Phase 3 — InstallableRail mounts replacing #4400 bespoke integration
  // list. scopeId resolves the org UUID from root.orgs by matching the
  // workspace owner slug.
  IntegrationsRail: {
    extends: 'InstallableRail',
    domain: 'integration',
    scopeType: 'org',
    scopeId: (el, s) => {
      const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
      return org?.id || org?._id || null
    },
    title: 'Integrations',
    layout: 'grid',
    emptyHint: 'Add an integration available to your whole org',
    marginTop: 'Y'
  },

  TemplatesRail: {
    extends: 'InstallableRail',
    domain: 'template',
    scopeType: 'org',
    scopeId: (el, s) => {
      const org = (s.root?.orgs || []).find((o) => o.slug === s.owner)
      return org?.id || org?._id || null
    },
    title: 'Featured templates',
    layout: 'grid',
    filter: { category: 'featured' },
    emptyHint: 'Browse and pin templates for quick access',
    marginTop: 'Y'
  }
}

export const WorkspaceThumb = {
  flow: 'column',
  gap: 'W',
  flex: '1 1 0',
  minWidth: '0',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  round: 'A',
  overflow: 'hidden',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.03)',
  transition: 'B transform defaultBezier, B box-shadow defaultBezier',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
  },

  onClick: (e, el, s) => {
    const owner = ownerSlug(s)
    const key = (s.key || '').split('.')[0]
    if (owner && key) el.router(`/${owner}/${key}`, el.getRoot())
  },

  Thumbnail: {
    width: '100%',
    aspectRatio: '16 / 10',
    // Image priority: explicit icon → stored thumbnail → rasterized
    // screenshot from `screenshots.symbo.ls` (prod only) → gradient
    // placeholder.
    background: (el, s) => {
      const img = s.icon?.src || s.thumbnail || projectScreenshotUrl(s)
      return img
        ? `center / cover no-repeat url("${img}")`
        : 'linear-gradient(135deg,#2a2f38 0%,#1a1d24 55%,#0f1116 100%)'
    },
    position: 'relative',

    Placeholder: {
      // Always keep the letter fallback visible behind the image so a
      // failed screenshot fetch still shows something intelligible.
      if: (el, s) => !(s.icon?.src || s.thumbnail),
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(255,255,255,0.5)',
      fontSize: 'B2',
      fontWeight: '700',
      text: (el, s) => (s.name || s.key || '?').charAt(0).toUpperCase()
    }
  },

  Meta: {
    flow: 'column',
    gap: 'W',
    padding: 'Y2 Y2 A Y2',

    Name: {
      tag: 'span',
      text: (el, s) => s.name || s.key,
      fontSize: 'Z1',
      fontWeight: '600',
      color: 'white',
      style: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    },
    Key: {
      tag: 'span',
      text: (el, s) => s.key || '',
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.45)',
      fontFamily: 'monospace'
    }
  }
}
